import { httpsCallable, type FunctionsError } from "firebase/functions";
import { collection, doc, getDoc, getDocs, onSnapshot, orderBy, query, where, type Timestamp } from "firebase/firestore";
import { parseCoachingConfig, type CoachingConfig } from "@coaching/config";
import { coachPhotoUrl } from "@coaching/photo";
import type { CoachProfile, CoachingSubmission, Moment, ReviewDraft, SubmissionMedia, VoiceNote } from "@coaching/types";
import { db, functions } from "./firebase";

export type Submission = CoachingSubmission<Timestamp> & { id: string };
export type Coach = CoachProfile<Timestamp>;

/** A callable failure with the code the server chose, for copy that can act on it. */
export class ApiError extends Error {
    constructor(public readonly code: string, message: string, public readonly details: unknown) {
        super(message);
    }
}

async function call<Req, Res>(name: string, data: Req): Promise<Res> {
    try {
        const fn = httpsCallable<Req, Res>(functions, name);
        return (await fn(data)).data;
    } catch (err) {
        const e = err as FunctionsError;
        const code = typeof e.code === "string" ? e.code.replace(/^functions\//, "") : "unknown";
        throw new ApiError(code, e.message || "Something went wrong.", e.details);
    }
}

/** The coach's review calls go through the one function kept warm, so none waits on a cold start. */
const request = <Req extends object, Res>(action: string, data: Req) => call<Req & { action: string }, Res>("coachingRequest", { action, ...data });

export const api = {
    respond: (submissionId: string, response: "accept" | "decline", message?: string) =>
        request<{ submissionId: string; response: string; message?: string }, { ok: true; status: string }>("respond", { submissionId, response, message }),
    saveDraft: (submissionId: string, draft: ReviewDraft) =>
        request<{ submissionId: string; draft: ReviewDraft }, { revision: number; status?: string }>("saveReview", { submissionId, draft }),
    returnReview: (submissionId: string) => request<{ submissionId: string }, { ok: true }>("returnReview", { submissionId }),
    media: (submissionId: string) => request<{ submissionId: string }, SubmissionMedia>("media", { submissionId }),
    /** A moment note or the summary developed into fuller coaching feedback, for the coach to use or ignore. */
    improve: (submissionId: string, kind: "note" | "summary", text: string, momentTitle = "") =>
        request<{ submissionId: string; kind: string; text: string; momentTitle: string }, { text: string }>("improve", { submissionId, kind, text, momentTitle }),
    /** A recorded voice note, base64; the server re-encodes and stores it and returns its key and length. */
    uploadVoice: (submissionId: string, audio: string) => request<{ submissionId: string; audio: string }, VoiceNote>("voice", { submissionId, audio }),
    updateProfile: (patch: Partial<Pick<Coach, "displayName" | "contactEmail" | "contactPhone" | "priceTier" | "headline" | "bio" | "experienceYears" | "specialties" | "certifications" | "languages" | "acceptingSubmissions" | "maxOpen" | "turnaroundDays" | "workingDays">>) =>
        call<typeof patch, { ok: true }>("updateCoachProfile", patch),
};

export function watchInbox(coachId: string, onChange: (rows: Submission[]) => void, onError: (e: Error) => void): () => void {
    const q = query(collection(db, "coachingSubmissions"), where("coachId", "==", coachId), orderBy("timing.createdAt", "desc"));
    return onSnapshot(q, (snap) => onChange(snap.docs.map((d) => ({ id: d.id, ...(d.data() as CoachingSubmission<Timestamp>) }))), onError);
}

export function watchSubmission(id: string, onChange: (s: Submission | null) => void, onError: (e: Error) => void): () => void {
    return onSnapshot(doc(db, "coachingSubmissions", id), (snap) => {
        onChange(snap.exists() ? { id: snap.id, ...(snap.data() as CoachingSubmission<Timestamp>) } : null);
    }, onError);
}

/** Moments in bucket order: the server numbers `order` by clip, then time. */
export async function loadMoments(id: string): Promise<Moment[]> {
    const snap = await getDocs(collection(db, "coachingSubmissions", id, "moments"));
    return snap.docs
        .map((d) => d.data() as Moment & { updatedAt?: unknown })
        .map(({ updatedAt: _u, ...m }) => m)
        .sort((a, b) => a.order - b.order || a.t - b.t);
}

export function watchCoach(uid: string, onChange: (c: Coach | null) => void, onError: (e: Error) => void): () => void {
    return onSnapshot(doc(db, "coaches", uid), (snap) => onChange(snap.exists() ? (snap.data() as Coach) : null), onError);
}

/** The coach's Swana app photo, live, through the same rule the coach list uses. */
export function watchAppPhoto(uid: string, onChange: (url: string | null) => void, onError: (e: Error) => void): () => void {
    return onSnapshot(doc(db, "users", uid), (snap) => onChange(coachPhotoUrl(snap.data(), null)), onError);
}

/** The limits and the promise, read once per page; the same parser the app and the functions use. */
export async function loadCoachingConfig(): Promise<CoachingConfig> {
    const snap = await getDoc(doc(db, "config", "coaching"));
    return parseCoachingConfig(snap.exists() ? snap.data() : undefined);
}
